import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FaInstagram, FaPhoneAlt, FaTimes, FaWhatsapp } from 'react-icons/fa';
import api, { formatDateTime, formatPhone } from '../services/api';
import ChatModal from '../components/ChatModal';
import RecordingModal from '../components/RecordingModal';
import { BranchTag, SettingsBranchSelect, useEngagementScope } from '../context/EngagementScope';
import { ChipRow, INSTAGRAM_COLOR, Loader, Pagination, SearchBox, VOICE_COLOR, WHATSAPP_GREEN, contactLabel, usePagination } from '../components/ui';

const STATUS_CHIPS = [
  { key: 'open', label: 'Open requests' },
  { key: 'resolved', label: 'Resolved' },
];

const CHANNEL_CHIPS = [
  { key: '', label: 'All channels' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'voice', label: 'Voice call' },
];

const CHANNELS = {
  whatsapp: { label: 'WhatsApp', color: WHATSAPP_GREEN, icon: <FaWhatsapp size={17} /> },
  instagram: { label: 'Instagram', color: INSTAGRAM_COLOR, icon: <FaInstagram size={17} /> },
  voice: { label: 'Voice call', color: VOICE_COLOR, icon: <FaPhoneAlt size={14} /> },
};

function ChannelTag({ channel }) {
  const cfg = CHANNELS[channel] || CHANNELS.whatsapp;
  return (
    <span title={cfg.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: cfg.color, fontWeight: 600, fontSize: 12 }}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// Staff numbers that get a WhatsApp alert for every new request.
function AlertNumbers() {
  const [settings, setSettings] = useState(null);
  const { selectedIds, branchName, scopeType } = useEngagementScope();
  const [salon, setSalon] = useState(() => (selectedIds.length === 1 ? selectedIds[0] : ''));
  const [numbers, setNumbers] = useState([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const apply = (s) => {
    setSettings(s);
    setNumbers(s.numbers || []);
  };

  useEffect(() => {
    let alive = true;
    setSettings(null);
    api
      .getAlertNumbers(salon)
      .then((s) => {
        if (alive) apply(s);
      })
      .catch((e) => {
        if (alive) toast.error(e.message || 'Could not load alert numbers');
      });
    return () => {
      alive = false;
    };
  }, [salon]);

  const dirty = settings && JSON.stringify(numbers) !== JSON.stringify(settings.numbers || []);

  const add = () => {
    const value = draft.trim();
    if (!value) return;
    if (numbers.length >= (settings?.max || 10)) {
      toast.error(`At most ${settings?.max || 10} numbers`);
      return;
    }
    setNumbers((list) => [...list, value]);
    setDraft('');
  };

  const save = async () => {
    setSaving(true);
    try {
      apply(await api.saveAlertNumbers(numbers, salon));
      toast.success('Alert numbers saved');
    } catch (e) {
      toast.error(e.message || 'Could not save the numbers');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { results } = await api.testAlert(salon);
      const failed = results.filter((r) => !r.ok);
      if (failed.length) toast.error(`Not sent to ${failed.map((r) => formatPhone(r.number)).join(', ')}: ${failed[0].error}`);
      else toast.success(`Test alert sent to ${results.length} number${results.length > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e.message || 'Could not send the test alert');
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return (
      <div className="de-panel" style={{ padding: 16, marginBottom: 16 }}>
        <SettingsBranchSelect value={salon} onChange={setSalon} />
        <Loader text="Loading alert numbers…" />
      </div>
    );
  }
  const canEdit = settings.can_edit !== false;
  const whose =
    scopeType === 'franchise'
      ? 'your branch'
      : salon
        ? branchName(salon)
        : 'the organization (branches without their own numbers, and requests with no branch yet)';
  return (
    <div className="de-panel" style={{ padding: 16, marginBottom: 16 }}>
      <SettingsBranchSelect value={salon} onChange={setSalon} />
      <div className="de-row de-row--between de-row--wrap" style={{ gap: 10, marginBottom: 6 }}>
        <div className="de-strong">Staff alert numbers · {whose}</div>
        <div className="de-small de-muted">
          {settings.whatsapp_ready ? `WhatsApp template ${settings.template}` : 'WhatsApp is not set up: alerts cannot be sent yet'}
        </div>
      </div>
      <div className="de-small de-muted" style={{ marginBottom: 12 }}>
        Each number gets a WhatsApp alert with the customer, their number, the channel (WhatsApp, Instagram or voice call) and the reason, as
        soon as a new request comes in.
        {settings.source === 'env' && !dirty ? ' These numbers come from the server settings until you save them here.' : ''}
        {salon && settings.source !== 'branch' && !dirty ? ' This branch has no numbers of its own yet: the organization’s numbers are used.' : ''}
        {!canEdit ? ' Only HQ can change them.' : ''}
      </div>
      <div className="de-row de-row--wrap" style={{ gap: 8, marginBottom: 12 }}>
        {numbers.length ? (
          numbers.map((n, i) => (
            <span key={`${n}-${i}`} className="de-pill de-pill--info" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {formatPhone(n) || n}
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Remove ${n}`}
                  onClick={() => setNumbers((list) => list.filter((_, j) => j !== i))}
                  style={{ border: 0, background: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'inline-flex' }}
                >
                  <FaTimes size={10} />
                </button>
              ) : null}
            </span>
          ))
        ) : (
          <span className="de-muted de-small">No alert numbers: requests only show on this page.</span>
        )}
      </div>
      <div className="de-row de-row--wrap" style={{ gap: 8, display: canEdit ? undefined : 'none' }}>
        <input
          className="de-input"
          style={{ maxWidth: 240 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Mobile number, e.g. 98400 12345"
          inputMode="tel"
        />
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={add} disabled={!draft.trim()}>
          Add
        </button>
        <button type="button" className="de-btn de-btn--primary de-btn--sm" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="de-btn de-btn--outline de-btn--sm"
          onClick={test}
          disabled={testing || dirty || !numbers.length || !settings.whatsapp_ready}
          title={dirty ? 'Save first' : 'Send a sample alert to these numbers'}
        >
          {testing ? 'Sending…' : 'Send test alert'}
        </button>
      </div>
    </div>
  );
}

export default function HumanIntervention() {
  const { multiBranch, isHQ } = useEngagementScope();
  const [status, setStatus] = useState('open');
  const [channel, setChannel] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [chatFor, setChatFor] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return api
      .listHandoffs(status)
      .then(setRows)
      .catch((e) => toast.error(e.message || 'Failed to load intervention requests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
  }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!channel || r.channel === channel) &&
        (!q ||
          [r.customer_name, r.customer_phone, r.reason, r.last_context, r.channel_label, ...(r.services || [])].some((v) =>
            String(v || '').toLowerCase().includes(q),
          )),
    );
  }, [rows, search, channel]);

  const { page, setPage, pageSize, setPageSize, paginated, totalPages } = usePagination(filtered, 25);

  const resolve = async (row) => {
    setBusy(row.id);
    try {
      await api.resolveHandoff(row.id);
      toast.success(row.channel === 'voice' ? 'Marked as resolved.' : 'Marked as resolved. The AI continues this chat.');
      await load(true);
    } catch (e) {
      toast.error(e.message || 'Could not mark as resolved');
    } finally {
      setBusy(null);
    }
  };

  const open = (row) => {
    if (row.channel === 'voice') {
      if (row.call) setCallFor({ ...row.call, customer_name: row.customer_name, customer_phone: row.customer_phone });
      else toast('This request has no call record: call the customer back on their number.');
    } else {
      setChatFor(row);
    }
  };

  return (
    <div className="de-fade">
      <div className="de-welcome" style={{ display: 'block' }}>
        <div className="de-welcome-title">Human Intervention Requests</div>
        <div className="de-welcome-sub">
          Alerts for the team from WhatsApp, Instagram and voice calls: customers who asked for a person or a call back, complaints, and chats the AI
          could not answer. An alert does not stop the AI. To take over a chat, open it and press Stop or reply; Mark resolved hands it back to the
          AI. Newest first.
        </div>
      </div>

      <AlertNumbers />

      <div className="de-panel" style={{ padding: 16, marginBottom: 16 }}>
        <ChipRow chips={STATUS_CHIPS} value={status} onChange={setStatus} />
        <ChipRow chips={CHANNEL_CHIPS} value={channel} onChange={setChannel} />
        <SearchBox value={search} onChange={setSearch} placeholder="Search name, phone, context…" />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="de-table-shell">
          <div className="de-list-head de-hi-head" style={{ borderRadius: 0 }}>
            <div className="de-hi-channel">Channel</div>
            <div className="de-hi-name">Name</div>
            <div className="de-hi-contact">Contact</div>
            <div className="de-hi-services">Services</div>
            <div className="de-hi-when">Requested</div>
            <div className="de-hi-context">Context</div>
            <div className="de-hi-actions">Actions</div>
          </div>
          {paginated.length === 0 ? (
            <div className="de-list-row" style={{ justifyContent: 'center', color: 'var(--de-soft)', padding: 26 }}>
              {status === 'open' ? 'No open requests' : 'No resolved requests yet'}
            </div>
          ) : (
            paginated.map((r) => (
              <div key={r.id} className="de-list-row de-hi-row">
                <div className="de-hi-channel">
                  <ChannelTag channel={r.channel} />
                </div>
                <div className="de-hi-name">
                  <div className="de-strong de-ellipsis">{r.customer_name || '—'}</div>
                  <div className="de-small de-gold-text de-ellipsis">{r.reason}</div>
                  {multiBranch || (isHQ && r.queue) ? <BranchTag franchiseId={r.franchise_id} name={r.branch_name} queue={r.queue} /> : null}
                  {r.agent_paused && r.channel !== 'voice' ? <span className="de-pill de-pill--warn de-pill--sm">Staff have taken over</span> : null}
                </div>
                <div className="de-hi-contact">{contactLabel(r.customer_phone, r.instagram_username)}</div>
                <div className="de-hi-services de-ellipsis de-muted">
                  {(r.services || []).length ? r.services.join(', ') : '—'}
                </div>
                <div className="de-hi-when de-small">
                  {formatDateTime(r.created_at)}
                  {r.messages_since ? <div className="de-pill de-pill--danger de-pill--sm" style={{ marginTop: 3 }}>{r.messages_since} waiting</div> : null}
                </div>
                <div className="de-hi-context de-ellipsis de-muted" title={r.last_context}>
                  {r.last_context || '—'}
                </div>
                <div className="de-hi-actions">
                  <button type="button" className="de-btn de-btn--primary de-btn--xs" onClick={() => open(r)}>
                    {r.channel === 'voice' ? 'View call' : 'View chat'}
                  </button>
                  {r.status === 'open' ? (
                    <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={() => resolve(r)} disabled={busy === r.id}>
                      {busy === r.id ? 'Saving…' : 'Mark resolved'}
                    </button>
                  ) : (
                    <span className="de-pill de-pill--ok">Resolved</span>
                  )}
                </div>
              </div>
            ))
          )}
          <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      )}

      {chatFor ? (
        <ChatModal
          customerId={chatFor.customer_id}
          channel={chatFor.channel || 'whatsapp'}
          conversationId={chatFor.conversation_id || null}
          onClose={() => setChatFor(null)}
          onChanged={() => load(true)}
        />
      ) : null}
      {callFor ? <RecordingModal call={callFor} onClose={() => setCallFor(null)} /> : null}
    </div>
  );
}
